import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Styles } from './styles';
import Grid from '@mui/material/Grid2';
import { useMetadataStore } from '@stores/Metadata/Metadata';
import CurrencyMaskedInput from '@components/CurrencyMaskedInput';

const GridItem = ({
  children,
  direction,
  ...props
}: {
  children: React.ReactNode;
  direction: 'column' | 'row';
}) => {
  return (
    <Grid
      container
      direction={direction}
      sx={{
        ...props,
        flex: 1,
        flexGrow: 1,
        height: 'max-content',
        flexDirection: 'column',
        alignContent: 'flex-start',
        justifyContent: 'space-around'
      }}>
      {children}
    </Grid>
  );
};

interface FilterProps {
  gridDirection?: 'column' | 'row';
  navigateTo?: string;
  showSearchField?: boolean;
}

function Filter({
  gridDirection = 'column',
  navigateTo = '/pesquisar',
  showSearchField = false
}: FilterProps) {
  const navigate = useNavigate();
  const { tiposImovel, cidades } = useMetadataStore();

  const tiposOrdenados = [...tiposImovel].sort((a, b) => a.ordem - b.ordem);
  const cidadesOrdenadas = [...cidades].sort((a, b) => a.ordem - b.ordem);
  const [filters, setFilters] = useState({
    tipoImovel: '',
    quartos: '',
    suites: '',
    cidade: '',
    operacao: '',
    valorMin: '',
    valorMax: '',
    termo: ''
  });
  const currentUrlFilters = React.useMemo(
    () => new URLSearchParams(location.search),
    []
  );
  React.useEffect(() => {
    setFilters({
      tipoImovel: currentUrlFilters.get('tipoImovel') || '',
      quartos: currentUrlFilters.get('quartos') || '',
      suites: currentUrlFilters.get('suites') || '',
      cidade: currentUrlFilters.get('cidade') || '',
      operacao: currentUrlFilters.get('operacao') || '',
      valorMin: currentUrlFilters.get('valorMin') || '',
      valorMax: currentUrlFilters.get('valorMax') || '',
      termo: currentUrlFilters.get('termo') || ''
    });
  }, [currentUrlFilters]);

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleSearch = () => {
    const selectedFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '')
    );
    const queryString = new URLSearchParams(selectedFilters).toString();
    navigate(`${navigateTo}?${queryString}`, { replace: true });
    navigate(`${navigateTo}?${queryString}`);
    setFilters({
      tipoImovel: '',
      quartos: '',
      suites: '',
      cidade: '',
      operacao: '',
      valorMin: '',
      valorMax: '',
      termo: ''
    });
    navigate(0);
  };

  return (
    <Styles.FiltroContainer>
      <Styles.HeaderFilter>
        <Styles.BoxTitle>
          <Styles.IconButton>
            <Styles.Icon />
          </Styles.IconButton>
          <Styles.Titulo>Busca de imóvel</Styles.Titulo>
        </Styles.BoxTitle>
      </Styles.HeaderFilter>

      <Styles.Filtros isVertical={gridDirection == 'column'}>
        <Grid
          container
          spacing={1}
          direction={gridDirection}
          sx={{
            flexGrow: 1,
            justifyContent: 'space-around',
            alignItems: 'stretch'
          }}>
          <GridItem direction={gridDirection}>
            <Styles.CampoFiltro>
              <Styles.Label htmlFor="tipo-imovel">Tipo de imóvel:</Styles.Label>
              <Styles.Select
                id="tipo-imovel"
                name="tipoImovel"
                value={filters.tipoImovel}
                onChange={handleFilterChange}>
                <Styles.Option value="">Selecione</Styles.Option>
                {tiposOrdenados.map((tipo) => (
                  <Styles.Option key={tipo.id} value={tipo.titulo}>
                    {tipo.titulo}
                  </Styles.Option>
                ))}
              </Styles.Select>
            </Styles.CampoFiltro>

            <Styles.CampoFiltro>
              <Styles.Label>Quartos:</Styles.Label>
              <Styles.Select
                id="quartos-quantidade"
                name="quartos"
                value={filters.quartos}
                onChange={handleFilterChange}>
                <Styles.Option value="">Selecione</Styles.Option>
                <Styles.Option value="1">1</Styles.Option>
                <Styles.Option value="2">2</Styles.Option>
                <Styles.Option value="3">3</Styles.Option>
                <Styles.Option value="4">4</Styles.Option>
                <Styles.Option value="5">5</Styles.Option>
                <Styles.Option value="6">6</Styles.Option>
                <Styles.Option value="7">7</Styles.Option>
                <Styles.Option value="8">8</Styles.Option>
                <Styles.Option value="9">9</Styles.Option>
                <Styles.Option value="10">10 ou mais</Styles.Option>
              </Styles.Select>
            </Styles.CampoFiltro>
          </GridItem>

          <GridItem direction={gridDirection}>
            <Styles.CampoFiltro>
              <Styles.Label>Suítes:</Styles.Label>
              <Styles.Select
                id="suites-quantidade"
                name="suites"
                value={filters.suites}
                onChange={handleFilterChange}>
                <Styles.Option value="">Selecione</Styles.Option>
                <Styles.Option value="1">1</Styles.Option>
                <Styles.Option value="2">2</Styles.Option>
                <Styles.Option value="3">3</Styles.Option>
                <Styles.Option value="4">4</Styles.Option>
                <Styles.Option value="5">5</Styles.Option>
                <Styles.Option value="6">6</Styles.Option>
                <Styles.Option value="7">7</Styles.Option>
                <Styles.Option value="8">8</Styles.Option>
                <Styles.Option value="9">9</Styles.Option>
                <Styles.Option value="10">10 ou mais</Styles.Option>
              </Styles.Select>
            </Styles.CampoFiltro>

            <Styles.CampoFiltro>
              <Styles.Label htmlFor="cidade">Cidade:</Styles.Label>
              <Styles.Select
                id="cidade"
                name="cidade"
                value={filters.cidade}
                onChange={handleFilterChange}>
                <Styles.Option value="">Selecione</Styles.Option>
                {cidadesOrdenadas.map((cidade) => (
                  <Styles.Option key={cidade.id} value={cidade.nome}>
                    {cidade.nome}
                  </Styles.Option>
                ))}
              </Styles.Select>
            </Styles.CampoFiltro>
          </GridItem>

          <GridItem direction={gridDirection}>
            <Styles.CampoFiltro>
              <Styles.Label>Operação:</Styles.Label>
              <Styles.OpcaoRadio isVertical={gridDirection == 'column'}>
                <Styles.RadioBox>
                  <Styles.RadioInput
                    type="radio"
                    id="alugar"
                    name="operacao"
                    value="ALUGUEL"
                    checked={filters.operacao == 'ALUGUEL'}
                    onChange={handleFilterChange}
                  />
                  <Styles.RadioLabel htmlFor="ALUGUEL">
                    Alugar
                  </Styles.RadioLabel>
                </Styles.RadioBox>
                <Styles.RadioBox>
                  <Styles.RadioInput
                    type="radio"
                    id="VENDA"
                    name="operacao"
                    value="VENDA"
                    checked={filters.operacao == 'VENDA'}
                    onChange={handleFilterChange}
                  />
                  <Styles.RadioLabel htmlFor="VENDA">Comprar</Styles.RadioLabel>
                </Styles.RadioBox>
              </Styles.OpcaoRadio>
            </Styles.CampoFiltro>
          </GridItem>

          <GridItem direction={gridDirection}>
            <Styles.CampoFiltro>
              <Styles.Label>Valor min:</Styles.Label>
              <CurrencyMaskedInput
                id="valor-min"
                name="valorMin"
                value={filters.valorMin ? Number(filters.valorMin) : null}
                placeholder="R$ 0"
                variant="filter"
                onChange={(value, rawValue) => {
                  setFilters((prev) => ({
                    ...prev,
                    valorMin: rawValue === '' ? '' : String(value)
                  }));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </Styles.CampoFiltro>

            <Styles.CampoFiltro>
              <Styles.Label>Valor max:</Styles.Label>
              <CurrencyMaskedInput
                id="valor-max"
                name="valorMax"
                value={filters.valorMax ? Number(filters.valorMax) : null}
                placeholder="R$ 0"
                variant="filter"
                onChange={(value, rawValue) => {
                  setFilters((prev) => ({
                    ...prev,
                    valorMax: rawValue === '' ? '' : String(value)
                  }));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </Styles.CampoFiltro>
          </GridItem>
        </Grid>

        {showSearchField && gridDirection === 'column' && (
          <Styles.CampoFiltro>
            <Styles.Label htmlFor="termo-busca">Buscar por texto:</Styles.Label>
            <Styles.Input
              type="text"
              id="termo-busca"
              placeholder="Ex: código, bairro, palavra-chave..."
              name="termo"
              value={filters.termo}
              onChange={handleFilterChange}
            />
          </Styles.CampoFiltro>
        )}

        {gridDirection === 'row' ? (
          <Styles.RowActions isCampoFiltro={showSearchField}>
            {showSearchField && (
              <Styles.CampoFiltro
                style={{ justifyContent: 'stretch', marginTop: '1rem' }}>
                <Styles.Label
                  htmlFor="termo-busca-horizontal"
                  style={{ marginBottom: 4 }}>
                  Buscar:
                </Styles.Label>
                <Styles.Input
                  style={{ flex: 1, maxWidth: 'unset' }}
                  type="text"
                  id="termo-busca-horizontal"
                  placeholder="Ex: código, bairro, palavra-chave..."
                  name="termo"
                  value={filters.termo}
                  onChange={handleFilterChange}
                  //checks if the enter key is pressed to trigger the search
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </Styles.CampoFiltro>
            )}
            <Styles.Button type="button" onClick={handleSearch}>
              Pesquisar
            </Styles.Button>
          </Styles.RowActions>
        ) : (
          <Styles.Button type="button" onClick={handleSearch}>
            Pesquisar
          </Styles.Button>
        )}
      </Styles.Filtros>
    </Styles.FiltroContainer>
  );
}

export default Filter;
